import React, { useState, useEffect, useMemo } from "react";
import { useParams } from "react-router-dom";
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "@/components/ui/card";
import { Skeleton } from "@/components/ui/skeleton";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Separator } from "@/components/ui/separator";
import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
  DialogFooter,
  DialogDescription,
} from "@/components/ui/dialog";
import { yupResolver } from "@hookform/resolvers/yup";
import {
  Sheet,
  SheetContent,
  SheetDescription,
  SheetHeader,
  SheetTitle,
  SheetTrigger,
  SheetFooter,
  SheetClose,
} from "@/components/ui/sheet";
import {
  Command,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
} from "@/components/ui/command";
import {
  ArrowLeft,
  Mail,
  Phone,
  Edit,
  Calendar,
  Percent,
  CreditCard,
  Plus,
  Trash2,
  FilePlus,
  Activity,
  Store,
  Link,
  FileText,
  Unlink,
  Check,
  X,
  Download,
  Upload,
  Loader2,
} from "lucide-react";
import { Link as RouterLink } from "react-router-dom";
import { formatDistanceToNow } from "date-fns";
import { Input } from "@/components/ui/input";
import {
  Form,
  FormField,
  FormItem,
  FormLabel,
  FormControl,
  FormMessage,
} from "@/components/ui/form";
import { useForm } from "react-hook-form";
import { toast } from "sonner";
import { Switch } from "@/components/ui/switch";
import { DataTable } from "@/components/ui/data-table";
import { CountrySelect } from "@/components/ui/country-select";
import { Badge } from "@/components/ui/badge";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/components/ui/popover";
import { DelinkConfirmationDialog } from "@/components/program/DelinkConfirmationDialog";
import { ContactDeleteConfirmationDialog } from "@/components/program/ContactDeleteConfirmationDialog";
import { APIClient, ClientApi, ProgramApi } from "@/api";
import {
  getDaysByFrequency,
  getFileUrl,
  getFrequencyByDays,
  isValidDate,
} from "@/utils";
import * as Yup from "yup";
import {
  Select,
  SelectTrigger,
  SelectContent,
  SelectItem,
  SelectValue,
} from "@/components/ui/select";
import {
  PAYMENT_CURRENCY_OPTIONS,
  PROGRAM_TYPE_OPTIONS,
} from "@/constants/program.constant";
import countries from "@/constants/array/country_city.json";
import { DocumentApi, DocumentUploadResponse } from "@/api/document.api";
import { LoaderButton } from "@/components/ui/loader-button";
import VirtualizedCitySelect from "@/components/ui/virtualized-city-select";

const contactSchema = Yup.object().shape({
  id: Yup.string().optional(),
  programId: Yup.string().required("ProgramId is required"),
  name: Yup.string()
    .required("Contact name is required")
    .matches(/^[A-Za-z ]+$/, "Only letters and spaces are allowed"),
  email: Yup.string()
    .required("Email is required")
    .email("Email must be a valid email address"),
  role: Yup.string()
    .required("Role is required")
    .matches(/^[A-Za-z ]+$/, "Only letters and spaces are allowed"),
  phoneCountry: Yup.string().required("Country code is required"),
  phoneNumber: Yup.string()
    .required("Contact number is required")
    .matches(/^\d+$/, "Phone must contain digits only")
    .min(7, "Phone number must be at least 7 digits")
    .max(15, "Phone number cannot exceed 15 digits"),
});

const documentUploadSchema = Yup.object().shape({
  documentName: Yup.string().required("Document name is required"),
  file: Yup.mixed().required("File is required"),
});

const RewardProgramDetails = () => {
  // In a real app, we would fetch the program details based on the ID
  const { id } = useParams<{ id: string }>();
  const [documents, setDocuments] = useState<any[]>([]);
  const [isAddContactDialogOpen, setIsAddContactDialogOpen] = useState(false);
  const [isAddMerchantDialogOpen, setIsAddMerchantDialogOpen] = useState(false);
  const [isEditProgramSheetOpen, setIsEditProgramSheetOpen] = useState(false);
  const [merchants, setMerchants] = useState([]);
  const [viewType, setViewType] = useState<"card" | "table">("card");
  const [selectedMerchants, setSelectedMerchants] = useState<string[]>([]);
  const [searchQuery, setSearchQuery] = useState("");
  const [isLinkConfirmationOpen, setIsLinkConfirmationOpen] = useState(false);
  const [isDelinkConfirmationOpen, setIsDelinkConfirmationOpen] =
    useState(false);
  const [merchantToDelink, setMerchantToDelink] = useState<string>("");
  const [isContactDeleteConfirmationOpen, setIsContactDeleteConfirmationOpen] =
    useState(false);
  const [contactToDelete, setContactToDelete] = useState<any>(null);
  const [selectedCountry, setSelectedCountry] = useState(null);
  const [programDetails, setProgramDetails] = useState<any>({});
  const [programContacts, setProgramContacts] = useState<any>([]);
  const [phoneInputs, setPhoneInputs] = useState([
    { programContactId: "", phoneCountryCode: "", phoneNumber: "" },
  ]);
  const [preview, setPreview] = useState(null);
  const [clients, setClients] = useState([]);
  const [availableMerchants, setAvailableMerchants] = useState([
    {
      id: "mer10",
      name: "The Entertainer",
      logo: "/placeholder.svg",
      type: "Online",
      address: "Dubai Media City",
      contactPerson: "Mark Thompson",
      email: "mark@entertainer.ae",
      phone: "+971-4-111-2222",
      status: "Active",
    },
    {
      id: "mer11",
      name: "VOX Cinemas",
      logo: "/placeholder.svg",
      type: "Offline",
      address: "Mall of the Emirates, Dubai",
      contactPerson: "Sarah Johnson",
      email: "sarah@voxcinemas.com",
      phone: "+971-4-222-3333",
      status: "Active",
    },
    {
      id: "mer12",
      name: "Carrefour UAE",
      logo: "/placeholder.svg",
      type: "Offline",
      address: "Multiple locations, UAE",
      contactPerson: "Ahmed Al Nasser",
      email: "ahmed@carrefour.ae",
      phone: "+971-4-333-4444",
      status: "Active",
    },
    {
      id: "mer13",
      name: "Noon.com",
      logo: "/placeholder.svg",
      type: "Online",
      address: "www.noon.com",
      contactPerson: "Reem Al Hashmi",
      email: "reem@noon.com",
      phone: "+971-4-444-5555",
      status: "Active",
    },
    {
      id: "mer14",
      name: "Arabian Adventures",
      logo: "/placeholder.svg",
      type: "Offline",
      address: "Sheikh Zayed Road, Dubai",
      contactPerson: "James Wilson",
      email: "james@arabianadventures.com",
      phone: "+971-4-555-6666",
      status: "Active",
    },
  ]);
  const [activityLogs, setActivityLogs] = useState([]);
  const [activityLoading, setActivityLoading] = useState(false);
  const [activityError, setActivityError] = useState(null);
  const [isDocumentUploadOpen, setIsDocumentUploadOpen] = useState(false);
  const [isDocumentUploading, setIsDocumentUploading] = useState(false);
  const [downloadingDocumentId, setDownloadingDocumentId] = useState<
    number | null
  >(null);
  const [uploadedDocument, setUploadedDocument] =
    useState<DocumentUploadResponse | null>(null);
  const [isDeleteDocumentOpen, setIsDeleteDocumentOpen] = useState(false);
  const [documentToDelete, setDocumentToDelete] = useState<any>(null);
  const fileInputRef = React.useRef<HTMLInputElement>(null);
  const [selectedTab, setSelectedTab] = useState("details");
  const [isUpdating, setIsUpdating] = useState(false);

  async function getProgramDetailsById(programId) {
    try {
      const { data } = await ProgramApi.fetchProgram({ programId });
      // console.log("data?.programVOs", )
      setProgramDetails(data?.programVOs[0]);
    } catch (err) {
      console.log("err");
    }
  }

  const getProgramContactsByProgramId = async (id) => {
    await APIClient(`/program/getProgramContacts?programId=${id}`).then(
      (res) => {
        setProgramContacts(res.data);
        setPhoneInputs(
          res?.data?.map(
            ({ programContactPhoneVOList }) =>
              programContactPhoneVOList[0] || []
          )
        );
        // reset(generateDefaultValues(res.data));
      }
    );
  };

  const fetchDocumentsForProgram = async (programId: string) => {
    try {
      const { data } = await DocumentApi.getDocumentsForProgram(
        parseInt(programId)
      );
      if (data && Array.isArray(data)) {
        const formattedDocuments = data.map((doc: any) => ({
          id: doc.id,
          name: doc.originalFileName,
          uploadedBy: doc.uploadedBy,
          uploadedDate: doc.uploadDateTime,
          size: `${(doc.fileSize / (1024 * 1024)).toFixed(1)} MB`,
          fileUrl: doc.fileUrl,
          documentName: doc.documentName,
        }));
        setDocuments(formattedDocuments);
      }
    } catch (error) {
      setDocuments([]);
    }
  };

  async function fetchDataFromDb(id) {
    if (id) {
      getProgramDetailsById(id);
      getProgramContactsByProgramId(id);
      fetchDocumentsForProgram(id);
    }
  }
  useEffect(() => {
    fetchDataFromDb(id);
    getClients();
  }, []);

  const getClients = async () => {
    try {
      const res = await ClientApi.getAllClients();
      setClients(
        (res?.data || [])?.map((client) => ({
          value: `${client?.id || ""}`,
          label: client?.clientName,
        }))
      );
    } catch (error) {
      toast.error(error?.message || "Failed to fetch clients");
    }
  };

  function generateDefaultValues(data) {
    const country = (countries as any)?.find(
      (country) => country?.name === data?.country
    );
    return {
      id: data?.id,
      vatNumber: data?.taxId || "",
      country: country ? JSON.stringify(country) : "",
      clientId: `${data?.clientId || ""}`,
      city: data?.city || "",
      programType: data?.programType || "",
      programName: data?.programName || "",
      address: data?.address || "",
      costPerPoint: `${data?.costPerPoint || 0}`?.replace("$", ""),
      cashbackPercentage: `${
        data.percentageOfCashbackToBeAccrued || 0
      }`.replace("%", ""),
      loyaltyPointsName: data?.loyaltyCurrencyId || "",
      programCode: data?.programCode || "",
      paymentCurrency: data?.currencyVO?.iso3CurrencyCode || "",
      domain: data?.domain || "",
      websiteUrl: data?.websiteUrl || "",
      percentageOfCashbackToBeAccrued:
        data?.percentageOfCashbackToBeAccrued || "",
      currencyVO: data?.currencyVO?.id || "",
      invoiceFrequency: getFrequencyByDays(data?.invoiceFrequencyInDays || ""),
      // contacts: contactDetails || [],
      txnCommissionPctChargedToPrg: data?.txnCommissionPctChargedToPrg || "",
      offerDenomination: data?.offerDenomination || "",
      legalCompanyName: data?.clientVO?.clientName || "",
    };
  }

  const defaultValues = useMemo(
    () => generateDefaultValues(programDetails),
    [programDetails]
  );
  // Form for editing program details
  const programForm = useForm({
    defaultValues,
  });

  const {
    reset,
    formState: { errors },
  } = programForm;

  // Function to check if a tab has validation errors
  const getTabErrors = () => {
    const tabErrors = {
      general: false,
      loyalty: false,
      payment: false,
      address: false,
    };

    // General tab errors
    if (
      errors.programName ||
      errors.programCode ||
      errors.programType ||
      errors.websiteUrl ||
      errors.domain ||
      errors.clientId
    ) {
      tabErrors.general = true;
    }

    // Loyalty tab errors
    if (
      errors.costPerPoint ||
      errors.cashbackPercentage ||
      errors.loyaltyPointsName ||
      errors.txnCommissionPctChargedToPrg ||
      errors.offerDenomination
    ) {
      tabErrors.loyalty = true;
    }

    // Payment tab errors
    if (errors.paymentCurrency || errors.invoiceFrequency) {
      tabErrors.payment = true;
    }

    // Address tab errors
    if (errors.country || errors.city || errors.address || errors.vatNumber) {
      tabErrors.address = true;
    }

    return tabErrors;
  };

  const tabErrors = getTabErrors();

  useEffect(() => {
    if (programDetails) {
      const defaults = generateDefaultValues(programDetails);
      reset(defaults);

      // Set selectedCountry based on the program's country
      const country = (countries as any)?.find(
        (country) => country?.name === programDetails?.country
      );
      if (country) {
        setSelectedCountry(country);
      }
    }
  }, [programDetails, reset]);
  // Form for adding new contact
  const contactForm = useForm({
    resolver: yupResolver(contactSchema),
    defaultValues: {
      name: "",
      email: "",
      role: "",
      id: "",
      programId: id,
      phoneCountry: "",
      phoneNumber: "",
    },
  });

  // Form for adding new merchant - now just handles the submission
  const merchantForm = useForm({
    defaultValues: {
      selectedMerchants: [] as string[],
    },
  });

  const documentUploadForm = useForm({
    resolver: yupResolver(documentUploadSchema),
    defaultValues: {
      documentName: "",
      file: null as File | null,
    },
  });

  const [addingContact, setAddingContact] = useState(false);

  const handleAddContact = async (data: any) => {
    setAddingContact(true);
    try {
      const contactData = {
        contactEmail: data?.email,
        contactName: data?.name,
        contactRole: data?.role,
        id: data?.id,
        programContactPhoneVOList: [
          {
            phoneNumber: data?.phoneNumber,
            phoneCountryCode: data?.phoneCountry,
            ...(data?.id && { programContactId: data?.id }),
          },
        ],
        programId: id,
      };
      const { data: responseData } = await addContact(contactData);
      setProgramContacts([...programContacts, responseData]);
      setIsAddContactDialogOpen(false);
      contactForm.reset();
      toast.success("Contact added successfully");
    } catch (err) {
      toast.error(err?.message || "Failed to update contacts");
    } finally {
      setAddingContact(false);
    }
  };

  const handleAddMerchant = () => {
    if (selectedMerchants.length === 0) {
      toast.error("Please select at least one merchant");
      return;
    }

    setIsLinkConfirmationOpen(true);
  };

  const confirmLinkMerchants = () => {
    const newMerchants = selectedMerchants
      .map((merchantId) => {
        const merchantData = availableMerchants.find(
          (m) => m.id === merchantId
        );
        if (!merchantData) return null;

        return {
          id: merchantData.id,
          name: merchantData.name,
          logo: merchantData.logo,
          type: merchantData.type,
          address: merchantData.address,
          contactPerson: merchantData.contactPerson,
          email: merchantData.email,
          phone: merchantData.phone,
          status: "Active",
        };
      })
      .filter(Boolean);

    setMerchants([...merchants, ...(newMerchants as any[])]);
    setIsAddMerchantDialogOpen(false);
    setIsLinkConfirmationOpen(false);
    setSelectedMerchants([]);
    toast.success(`${newMerchants.length} merchant(s) linked successfully`);
  };

  const handleFileChange = (event) => {
    const selectedFile = event.target.files[0];
    if (selectedFile) {
      setPreview(selectedFile);
    }
  };

  const handlePlusIconClick = () => {
    document.getElementById("fileInput").click();
  };

  const handleUpdateProgram = async (formData) => {
    setIsUpdating(true);
    try {
      const { contacts, ...updatedData } = formData;
      const currency = PAYMENT_CURRENCY_OPTIONS?.find(
        (currency) => currency?.value === formData?.paymentCurrency
      );
      const formDataToSend = new FormData();
      if (preview) {
        formDataToSend.append("programLogo", preview);
      }

      console.log(
        "formData?.clientId || programDetails?.clientId",
        formData?.clientId,
        programDetails?.clientId
      );
      const programData = {
        id: programDetails?.id,
        // ...updatedData,
        programName: formData?.programName,
        programType: formData?.programType,
        clientId: formData?.clientId,
        taxId: formData?.vatNumber,
        websiteUrl: formData?.websiteUrl,
        domain: formData?.domain,

        loyaltyCurrencyId: formData?.loyaltyPointsName,
        costPerPoint: formData?.costPerPoint,
        percentageOfCashbackToBeAccrued: formData?.cashbackPercentage,

        ...(currency && { currencyVO: { id: currency?.id } }),
        invoiceFrequencyInDays: getDaysByFrequency(formData?.invoiceFrequency),
        txnCommissionPctChargedToPrg:
          formData?.txnCommissionPctChargedToPrg || "",
        offerDenomination: formData?.offerDenomination || "",

        address: formData?.address,
        country: selectedCountry?.name,
        city: formData?.city,
      };
      const blob = new Blob([JSON.stringify(programData)], {
        type: "application/json",
      });
      formDataToSend.append("programVO", blob);

      await APIClient("/program/createOrUpdateProgram", formDataToSend, {
        requestType: "post",
        contentType: "multipart/form-data",
      });
      fetchDataFromDb(id);

      // handleReset();
      toast.success("Updated successfully");
      setIsEditProgramSheetOpen(false);
    } catch (error) {
      toast.error(error?.message || "Failed to update contacts");
    } finally {
      setIsUpdating(false);
    }
  };

  const handleDeleteContact = (contact: any) => {
    setContactToDelete(contact);
    setIsContactDeleteConfirmationOpen(true);
  };

  const confirmDeleteContact = async () => {
    try {
      await deleteContact(contactToDelete.id);
      setProgramContacts(
        programContacts.filter((contact) => contact.id !== contactToDelete.id)
      );
      toast.success("Contact deleted successfully");
    } catch (err) {
      toast.error(err?.message || "Failed to delete contact");
    }
  };

  const handleDelinkMerchant = (merchantId: string) => {
    setMerchantToDelink(merchantId);
    setIsDelinkConfirmationOpen(true);
  };

  const confirmDelinkMerchant = () => {
    setMerchants(
      merchants.filter((merchant) => merchant.id !== merchantToDelink)
    );
    setIsDelinkConfirmationOpen(false);
    setMerchantToDelink("");
    toast.success("Merchant delinked successfully");
  };

  const addContact = (contact) => {
    return APIClient(
      "/program/createOrUpdateProgramContact",
      {
        programId: id,
        ...contact,
      },
      { requestType: "post" }
    );
  };

  const deleteContact = (programContactId) => {
    return APIClient(
      `/program/deleteProgramContact?programContactId=${programContactId}`
    );
  };

  const handleFileUpload = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const files = e.target.files;
    if (files && files.length > 0) {
      const file = files[0];
      documentUploadForm.setValue("file", file);
      setIsDocumentUploadOpen(true);
    }
  };

  const handleDocumentUpload = async (data: any) => {
    setIsDocumentUploading(true);
    try {
      const file = data.file;
      const documentName = data.documentName;
      const { data: response } = await DocumentApi.uploadDocumentForProgram(
        file,
        {
          programId: parseInt(id),
          documentName: documentName,
        }
      );
      const newDocument = {
        id: response.id,
        name: response.originalFileName,
        uploadedBy: response.uploadedBy,
        uploadedDate: response.uploadDateTime,
        size: `${(response.fileSize / (1024 * 1024)).toFixed(1)} MB`,
        fileUrl: response.fileUrl,
        documentName: response.documentName,
      };
      setDocuments([...documents, newDocument]);
      setUploadedDocument(response);
      setIsDocumentUploadOpen(false);
      documentUploadForm.reset();
      toast.success(
        `Document "${response.originalFileName}" uploaded successfully`
      );
    } catch (error) {
      toast.error(error?.message || "Failed to upload document");
    } finally {
      setIsDocumentUploading(false);
    }
  };

  const handleDownloadDocument = async (doc: any) => {
    setDownloadingDocumentId(doc.id);
    try {
      const response = await DocumentApi.downloadDocument(doc.id);
      const blob = new Blob([response.data]);
      const url = window.URL.createObjectURL(blob);
      const a = window.document.createElement("a");
      a.href = url;
      a.download = doc.name;
      window.document.body.appendChild(a);
      a.click();
      window.URL.revokeObjectURL(url);
      window.document.body.removeChild(a);
      toast.success(`Document "${doc.name}" downloaded successfully`);
    } catch (error) {
      toast.error(error?.message || "Failed to download document");
    } finally {
      setDownloadingDocumentId(null);
    }
  };

  const handleDeleteDocument = async (document: any) => {
    setDocumentToDelete(document);
    setIsDeleteDocumentOpen(true);
  };

  const confirmDeleteDocument = async () => {
    if (!documentToDelete) return;
    try {
      await DocumentApi.deleteDocument(documentToDelete.id);
      setDocuments(documents.filter((doc) => doc.id !== documentToDelete.id));
      toast.success(`Document "${documentToDelete.name}" deleted successfully`);
    } catch (error) {
      toast.error(error?.message || "Failed to delete document");
    } finally {
      setIsDeleteDocumentOpen(false);
      setDocumentToDelete(null);
    }
  };

  // Toggle merchant selection
  const toggleMerchantSelection = (merchantId: string) => {
    if (selectedMerchants.includes(merchantId)) {
      setSelectedMerchants(selectedMerchants.filter((id) => id !== merchantId));
    } else {
      setSelectedMerchants([...selectedMerchants, merchantId]);
    }
  };

  // Function to format date as relative time (e.g., "1 day ago")
  const formatRelativeTime = (dateString: string) => {
    return formatDistanceToNow(new Date(dateString), { addSuffix: true });
  };

  const isPdfFile = (filename: string) => {
    return filename.toLowerCase().endsWith(".pdf");
  };

  // Filtered available merchants
  const filteredAvailableMerchants = availableMerchants.filter(
    (merchant) =>
      !merchants.some((m) => m.id === merchant.id) && // Not already linked
      merchant.name.toLowerCase().includes(searchQuery.toLowerCase()) // Matches search
  );

  // Merchant table columns
  const merchantColumns = [
    {
      header: "Merchant",
      cell: (row: any) => (
        <div className="flex items-center gap-3">
          <div className="w-10 h-10 rounded-md bg-gray-100 flex items-center justify-center">
            <img src={row.logo} alt={row.name} className="max-h-8 max-w-8" />
          </div>
          <div>
            <p className="font-medium text-gray-800">{row.name}</p>
            <p className="text-xs text-gray-500">{row.type} Merchant</p>
          </div>
        </div>
      ),
    },
    {
      header: "Contact Person",
      cell: (row: any) => (
        <div>
          <p className="text-gray-800">{row.contactPerson}</p>
          <p className="text-xs text-gray-500">{row.email}</p>
        </div>
      ),
    },
    {
      header: "Address",
      cell: (row: any) => (
        <p className="text-gray-800 truncate max-w-[200px]">{row.address}</p>
      ),
    },
    {
      header: "Status",
      cell: (row: any) => (
        <div
          className={`inline-flex px-2 py-1 rounded-full text-xs ${
            row.status === "Active"
              ? "bg-green-100 text-green-800"
              : "bg-gray-100 text-gray-800"
          }`}
        >
          {row.status}
        </div>
      ),
    },
    {
      header: "Actions",
      cell: (row: any) => (
        <Button
          variant="ghost"
          size="sm"
          className="text-gray-500 hover:text-red-600"
          onClick={() => handleDelinkMerchant(row.id)}
        >
          <Unlink className="h-4 w-4 mr-1" /> Delink
        </Button>
      ),
    },
  ];

  const fetchActivityLogs = async () => {
    setActivityLoading(true);
    setActivityError(null);
    try {
      const programCode = programDetails?.programCode;
      if (!programCode) {
        setActivityLogs([]);
        setActivityLoading(false);
        return;
      }
      const payload = {
        id: null,
        programCode,
        merchantCode: null,
        pageNumber: 0,
        pageSize: 50,
        sortOrder: "desc",
        sortBy: "createdAt",
      };
      const { data } = await ProgramApi.getActivityLogs(payload);
      setActivityLogs(data.activityLogVOs || []);
    } catch (err) {
      setActivityError("Failed to fetch activity logs");
    } finally {
      setActivityLoading(false);
    }
  };

  useEffect(() => {
    if (selectedTab === "activity" && programDetails?.programCode) {
      fetchActivityLogs();
    }
  }, [selectedTab, programDetails?.programCode]);

  return (
    <div className="space-y-6">
      {/* Header with back button */}
      <div className="flex items-center gap-4 mb-6">
        <Button variant="outline" size="sm" asChild className="bg-white">
          <RouterLink to="/rewardpoint/programs">
            <ArrowLeft className="h-4 w-4 mr-1" /> Back
          </RouterLink>
        </Button>
        <div>
          <h1 className="text-2xl font-bold text-gray-800">Program Details</h1>
          <p className="text-gray-500">View and manage program information</p>
        </div>
      </div>

      {/* Program header card */}
      <Card className="border-0 shadow-sm overflow-hidden bg-white">
        <CardContent className="p-6">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-5">
              <div className="h-20 w-20 bg-gray-100 rounded-lg flex items-center justify-center">
                <img
                  src={
                    programDetails?.programLogoUri
                      ? getFileUrl(programDetails?.programLogoUri)
                      : "/placeholder.svg"
                  }
                  alt={programDetails?.programName}
                  className="max-h-16 max-w-16"
                  onError={(e) => {
                    e.currentTarget.src = "/placeholder.svg";
                  }}
                />
              </div>
              <div>
                <h2 className="text-2xl font-bold text-gray-800">
                  {programDetails?.programName}
                </h2>
                <div className="flex items-center gap-3 mt-1 text-gray-500">
                  <span className="text-sm">
                    Code: {programDetails?.programCode}
                  </span>
                  {isValidDate(programDetails?.createdDateInMillis) && (
                    <span className="text-sm flex items-center gap-1">
                      <Calendar className="h-3.5 w-3.5" /> Created around{" "}
                      {formatRelativeTime(
                        new Date(programDetails?.createdDateInMillis) as any
                      )}
                    </span>
                  )}
                </div>
              </div>
            </div>
            <Sheet
              open={isEditProgramSheetOpen}
              onOpenChange={setIsEditProgramSheetOpen}
            >
              <SheetTrigger asChild>
                <Button>
                  <Edit className="h-4 w-4 mr-2" /> Edit Program
                </Button>
              </SheetTrigger>
              <SheetContent className="sm:max-w-md overflow-y-auto">
                <SheetHeader>
                  <SheetTitle>Edit Program</SheetTitle>
                  <SheetDescription>
                    Make changes to your program here. Click save when you're
                    done.
                  </SheetDescription>
                </SheetHeader>
                <div className="py-4">
                  <Tabs defaultValue="general" className="w-full">
                    <TabsList className="grid grid-cols-4 mb-4">
                      <TabsTrigger
                        value="general"
                        className={
                          tabErrors.general
                            ? "bg-red-100 text-red-800 border-red-300"
                            : ""
                        }
                      >
                        General
                      </TabsTrigger>
                      <TabsTrigger
                        value="loyalty"
                        className={
                          tabErrors.loyalty
                            ? "bg-red-100 text-red-800 border-red-300"
                            : ""
                        }
                      >
                        Loyalty
                      </TabsTrigger>
                      <TabsTrigger
                        value="payment"
                        className={
                          tabErrors.payment
                            ? "bg-red-100 text-red-800 border-red-300"
                            : ""
                        }
                      >
                        Payment
                      </TabsTrigger>
                      <TabsTrigger
                        value="address"
                        className={
                          tabErrors.address
                            ? "bg-red-100 text-red-800 border-red-300"
                            : ""
                        }
                      >
                        Address
                      </TabsTrigger>
                    </TabsList>

                    <Form {...programForm}>
                      <form
                        onSubmit={programForm.handleSubmit(handleUpdateProgram)}
                      >
                        <input
                          type="file"
                          id="fileInput"
                          name="programLogo"
                          style={{ display: "none", cursor: "pointer" }}
                          onChange={handleFileChange}
                        />
                        <TabsContent value="general" className="space-y-4 mt-4">
                          <div
                            className="h-20 w-20 bg-gray-100 rounded-lg flex items-center justify-center cursor-pointer hover:bg-gray-200"
                            onClick={handlePlusIconClick}
                          >
                            <img
                              src={
                                preview
                                  ? URL.createObjectURL(preview)
                                  : getFileUrl(programDetails?.programLogoUri)
                              }
                              alt={programDetails?.programName}
                              className="max-h-16 max-w-16 cursor-pointer"
                            />
                          </div>
                          <FormField
                            control={programForm.control}
                            name="programName"
                            render={({ field }) => (
                              <FormItem>
                                <FormLabel>Program Name</FormLabel>
                                <FormControl>
                                  <Input
                                    {...field}
                                    onChange={(e) => {
                                      const value = e.target.value.replace(
                                        /[^a-zA-Z0-9\s\-':.]/g,
                                        ""
                                      );
                                      field.onChange(value);
                                    }}
                                  />
                                </FormControl>
                              </FormItem>
                            )}
                          />

                          <FormField
                            control={programForm.control}
                            name="legalCompanyName"
                            render={({ field }) => (
                              <FormItem>
                                <FormLabel>Legal Company Name</FormLabel>
                                <FormControl>
                                  <Input {...field} />
                                </FormControl>
                              </FormItem>
                            )}
                          />

                          <FormField
                            control={programForm.control}
                            name="programType"
                            render={({ field }) => (
                              <FormItem>
                                <FormLabel>Program Type</FormLabel>
                                <Select
                                  onValueChange={field.onChange}
                                  defaultValue={field.value}
                                >
                                  <FormControl>
                                    <SelectTrigger>
                                      <SelectValue placeholder="Select program type" />
                                    </SelectTrigger>
                                  </FormControl>
                                  <SelectContent>
                                    {PROGRAM_TYPE_OPTIONS?.map((program) => (
                                      <SelectItem
                                        key={program?.id}
                                        value={program?.value}
                                      >
                                        {program?.label}
                                      </SelectItem>
                                    ))}
                                  </SelectContent>
                                </Select>
                                <FormMessage />
                              </FormItem>
                            )}
                          />

                          <FormField
                            control={programForm.control}
                            name="clientId"
                            render={({ field }) => (
                              <FormItem>
                                <FormLabel>Client</FormLabel>
                                <Select
                                  onValueChange={field.onChange}
                                  value={field.value}
                                  disabled={true}
                                >
                                  <FormControl>
                                    <SelectTrigger className="bg-gray-100">
                                      <SelectValue placeholder="Select Client" />
                                    </SelectTrigger>
                                  </FormControl>
                                  <SelectContent>
                                    {clients?.map((client) => (
                                      <SelectItem
                                        key={client?.value}
                                        value={client?.value}
                                      >
                                        {client?.label}
                                      </SelectItem>
                                    ))}
                                  </SelectContent>
                                </Select>
                                <FormMessage />
                              </FormItem>
                            )}
                          />

                          <FormField
                            control={programForm.control}
                            name="vatNumber"
                            render={({ field }) => (
                              <FormItem>
                                <FormLabel>VAT Number</FormLabel>
                                <FormControl>
                                  <Input {...field} />
                                </FormControl>
                              </FormItem>
                            )}
                          />

                          <FormField
                            control={programForm.control}
                            name="websiteUrl"
                            render={({ field }) => (
                              <FormItem>
                                <FormLabel>Website URL</FormLabel>
                                <FormControl>
                                  <Input {...field} />
                                </FormControl>
                              </FormItem>
                            )}
                          />

                          <FormField
                            control={programForm.control}
                            name="domain"
                            render={({ field }) => (
                              <FormItem>
                                <FormLabel>Domain name</FormLabel>
                                <FormControl>
                                  <Input {...field} />
                                </FormControl>
                              </FormItem>
                            )}
                          />
                        </TabsContent>

                        <TabsContent value="loyalty" className="space-y-4 mt-4">
                          <FormField
                            control={programForm.control}
                            name="loyaltyPointsName"
                            render={({ field }) => (
                              <FormItem>
                                <FormLabel>Loyalty Points Name</FormLabel>
                                <FormControl>
                                  <Input {...field} />
                                </FormControl>
                              </FormItem>
                            )}
                          />

                          <FormField
                            control={programForm.control}
                            name="costPerPoint"
                            render={({ field }) => (
                              <FormItem>
                                <FormLabel>Cost Per Point ($)</FormLabel>
                                <FormControl>
                                  <Input
                                    type="number"
                                    step="0.001"
                                    {...field}
                                  />
                                </FormControl>
                              </FormItem>
                            )}
                          />

                          <FormField
                            control={programForm.control}
                            name="cashbackPercentage"
                            render={({ field }) => (
                              <FormItem>
                                <FormLabel>
                                  Percentage of cashback to be Accrued (%)*
                                </FormLabel>
                                <FormControl>
                                  <Input type="number" step="0.01" {...field} />
                                </FormControl>
                              </FormItem>
                            )}
                          />
                        </TabsContent>

                        <TabsContent value="payment" className="space-y-4 mt-4">
                          <FormField
                            control={programForm.control}
                            name="paymentCurrency"
                            defaultValue={
                              programDetails?.currencyVO?.iso3CurrencyCode || ""
                            }
                            render={({ field }) => (
                              <FormItem>
                                <FormLabel>Payment Currency</FormLabel>
                                <FormControl>
                                  <select
                                    className="flex h-10 w-full rounded-md border border-input bg-background px-3 py-2 text-sm"
                                    {...field}
                                  >
                                    <option value="USD">USD</option>
                                    <option value="EUR">EUR</option>
                                    <option value="AED">AED</option>
                                    <option value="GBP">GBP</option>
                                  </select>
                                </FormControl>
                              </FormItem>
                            )}
                          />

                          <FormField
                            control={programForm.control}
                            name="invoiceFrequency"
                            render={({ field }) => (
                              <FormItem>
                                <FormLabel>Invoice Frequency</FormLabel>
                                <FormControl>
                                  <select
                                    className="flex h-10 w-full rounded-md border border-input bg-background px-3 py-2 text-sm"
                                    {...field}
                                  >
                                    <option value="weekly">Weekly</option>
                                    <option value="monthly">Monthly</option>
                                    <option value="quarterly">Quarterly</option>
                                    <option value="yearly">Yearly</option>
                                  </select>
                                </FormControl>
                              </FormItem>
                            )}
                          />

                          <FormField
                            control={programForm.control}
                            name="offerDenomination"
                            render={({ field }) => (
                              <FormItem>
                                <FormLabel>Denomination</FormLabel>
                                <FormControl>
                                  <Input placeholder="e.g. 10" {...field} />
                                </FormControl>
                                <FormMessage />
                              </FormItem>
                            )}
                          />
                          <FormField
                            control={programForm.control}
                            name="txnCommissionPctChargedToPrg"
                            render={({ field }) => (
                              <FormItem>
                                <FormLabel>Transaction Commission</FormLabel>
                                <FormControl>
                                  <Input placeholder="e.g. 12" {...field} />
                                </FormControl>
                                <FormMessage />
                              </FormItem>
                            )}
                          />
                        </TabsContent>
                        {/* Address Tab */}
                        <TabsContent value="address" className="space-y-4 mt-4">
                          <FormField
                            control={programForm.control}
                            name="address"
                            render={({ field }) => (
                              <FormItem>
                                <FormLabel>Address</FormLabel>
                                <FormControl>
                                  <Input {...field} />
                                </FormControl>
                              </FormItem>
                            )}
                          />

                          <FormField
                            control={programForm.control}
                            name="country"
                            render={({ field }) => (
                              <FormItem>
                                <FormLabel>Country*</FormLabel>
                                <Select
                                  onValueChange={(value) => {
                                    const selected = JSON.parse(value);
                                    setSelectedCountry(selected);
                                    field.onChange(value);
                                  }}
                                  value={field.value}
                                >
                                  <FormControl>
                                    <SelectTrigger>
                                      <SelectValue placeholder="Select Country" />
                                    </SelectTrigger>
                                  </FormControl>
                                  <SelectContent>
                                    {(countries as any)?.map((country) => (
                                      <SelectItem
                                        key={country?.name}
                                        value={JSON.stringify(country)}
                                      >
                                        {country?.name}
                                      </SelectItem>
                                    ))}
                                  </SelectContent>
                                </Select>
                                <FormMessage />
                              </FormItem>
                            )}
                          />

                          <FormField
                            control={programForm.control}
                            name="city"
                            render={({ field }) => (
                              <FormItem>
                                <FormLabel>City*</FormLabel>
                                <VirtualizedCitySelect
                                  cities={selectedCountry?.cities || []}
                                  value={field.value}
                                  onValueChange={field.onChange}
                                  placeholder="Select City"
                                  disabled={!selectedCountry?.cities?.length}
                                  // error={field.error?.message}
                                />
                                <FormMessage />
                              </FormItem>
                            )}
                          />
                        </TabsContent>

                        <SheetFooter className="mt-6">
                          <SheetClose asChild>
                            <Button type="button" variant="outline">
                              Cancel
                            </Button>
                          </SheetClose>
                          <LoaderButton type="submit" loading={isUpdating}>
                            Save changes
                          </LoaderButton>
                        </SheetFooter>
                      </form>
                    </Form>
                  </Tabs>
                </div>
              </SheetContent>
            </Sheet>
          </div>
        </CardContent>
      </Card>

      {/* Tabs for different sections */}
      <Tabs
        value={selectedTab}
        onValueChange={setSelectedTab}
        className="w-full"
      >
        <TabsList className="bg-white border-b border-gray-200 w-full justify-start rounded-none h-12 px-6">
          <TabsTrigger
            value="details"
            className="data-[state=active]:border-b-2 data-[state=active]:border-blue-600 rounded-none"
          >
            Program Details
          </TabsTrigger>
          <TabsTrigger
            value="contacts"
            className="data-[state=active]:border-b-2 data-[state=active]:border-blue-600 rounded-none"
          >
            Contacts
          </TabsTrigger>
          <TabsTrigger
            value="documents"
            className="data-[state=active]:border-b-2 data-[state=active]:border-blue-600 rounded-none"
          >
            Documents
          </TabsTrigger>
          <TabsTrigger
            value="activity"
            className="data-[state=active]:border-b-2 data-[state=active]:border-blue-600 rounded-none"
          >
            Activity
          </TabsTrigger>
        </TabsList>

        {/* Program Details Tab */}
        <TabsContent value="details" className="space-y-6 pt-4">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            {/* General Information */}
            <Card className="border-0 shadow-sm bg-white">
              <CardHeader className="border-b border-gray-100 bg-gray-50">
                <CardTitle className="text-lg font-medium text-gray-800">
                  General Information
                </CardTitle>
              </CardHeader>
              <CardContent className="p-6">
                <div className="space-y-4">
                  <div>
                    <p className="text-sm font-medium text-gray-500">
                      Program Type
                    </p>
                    <p className="text-gray-800">
                      {programDetails.programType}
                    </p>
                  </div>
                  <div>
                    <p className="text-sm font-medium text-gray-500">Client</p>
                    <p className="text-gray-800">
                      {programDetails.clientVO?.clientName}
                    </p>
                  </div>
                  <div>
                    <p className="text-sm font-medium text-gray-500">
                      VAT Number
                    </p>
                    <p className="text-gray-800">{programDetails.taxId}</p>
                  </div>
                  <div>
                    <p className="text-sm font-medium text-gray-500">
                      Website URL
                    </p>
                    <p className="text-gray-800">
                      {programDetails?.websiteUrl}
                    </p>
                  </div>
                  <div>
                    <p className="text-sm font-medium text-gray-500">
                      Domain name
                    </p>
                    <p className="text-gray-800">{programDetails?.domain}</p>
                  </div>
                </div>
              </CardContent>
            </Card>

            {/* Loyalty Details */}
            <Card className="border-0 shadow-sm bg-white">
              <CardHeader className="border-b border-gray-100 bg-gray-50">
                <CardTitle className="text-lg font-medium text-gray-800">
                  Loyalty Details
                </CardTitle>
              </CardHeader>
              <CardContent className="p-6">
                <div className="space-y-4">
                  <div className="flex items-start gap-2">
                    <div className="p-2 rounded-md bg-blue-50 text-blue-600 mt-1">
                      <CreditCard className="h-4 w-4" />
                    </div>
                    <div>
                      <p className="text-sm font-medium text-gray-500">
                        Loyalty Points Name
                      </p>
                      <p className="text-gray-800 font-medium">
                        {programDetails.loyaltyCurrencyId}
                      </p>
                    </div>
                  </div>
                  <div className="flex items-start gap-2">
                    <div className="p-2 rounded-md bg-blue-50 text-blue-600 mt-1">
                      <Percent className="h-4 w-4" />
                    </div>
                    <div>
                      <p className="text-sm font-medium text-gray-500">
                        Cost Per Point
                      </p>
                      <p className="text-gray-800 font-medium">
                        {programDetails?.costPerPoint}
                      </p>
                    </div>
                  </div>
                  <div className="flex items-start gap-2">
                    <div className="p-2 rounded-md bg-blue-50 text-blue-600 mt-1">
                      <Percent className="h-4 w-4" />
                    </div>
                    <div>
                      <p className="text-sm font-medium text-gray-500">
                        Percentage of cashback to be Accrued
                      </p>
                      <p className="text-gray-800 font-medium">
                        {programDetails?.percentageOfCashbackToBeAccrued}%
                      </p>
                    </div>
                  </div>
                </div>
              </CardContent>
            </Card>

            {/* Billing Information */}
            <Card className="border-0 shadow-sm bg-white">
              <CardHeader className="border-b border-gray-100 bg-gray-50">
                <CardTitle className="text-lg font-medium text-gray-800">
                  Billing Information
                </CardTitle>
              </CardHeader>
              <CardContent className="p-6">
                <div className="space-y-4">
                  <div>
                    <p className="text-sm font-medium text-gray-500">
                      Payment Currency
                    </p>
                    <p className="text-gray-800">
                      {programDetails?.currencyVO?.iso3CurrencyCode}
                    </p>
                  </div>
                  <div>
                    <p className="text-sm font-medium text-gray-500">
                      Invoice Frequency
                    </p>
                    <p className="text-gray-800 capitalize">
                      {getFrequencyByDays(
                        programDetails?.invoiceFrequencyInDays
                      )}
                    </p>
                  </div>
                  {programDetails?.offerDenomination > 0 && (
                    <div>
                      <p className="text-sm font-medium text-gray-500">
                        Denomination
                      </p>
                      <p className="text-gray-800">
                        {programDetails?.offerDenomination}
                      </p>
                    </div>
                  )}
                  {programDetails?.txnCommissionPctChargedToPrg ? (
                    <div>
                      <p className="text-sm font-medium text-gray-500">
                        Transaction Commission
                      </p>
                      <p className="text-gray-800">
                        {programDetails?.txnCommissionPctChargedToPrg}
                      </p>
                    </div>
                  ) : null}
                </div>
              </CardContent>
            </Card>

            {/* Program Address */}
            <Card className="border-0 shadow-sm bg-white">
              <CardHeader className="border-b border-gray-100 bg-gray-50">
                <CardTitle className="text-lg font-medium text-gray-800">
                  Program Address
                </CardTitle>
              </CardHeader>
              <CardContent className="p-6">
                <div className="space-y-4">
                  <div>
                    <p className="text-sm font-medium text-gray-500">Address</p>
                    <p className="text-gray-800">{programDetails.address}</p>
                  </div>
                  <div>
                    <p className="text-sm font-medium text-gray-500">City</p>
                    <p className="text-gray-800">{programDetails?.city}</p>
                  </div>
                  <div>
                    <p className="text-sm font-medium text-gray-500">Country</p>
                    <p className="text-gray-800">{programDetails?.country}</p>
                  </div>
                </div>
              </CardContent>
            </Card>
          </div>
        </TabsContent>

        {/* Contacts Tab */}
        <TabsContent value="contacts" className="space-y-6 pt-4">
          <Card className="border-0 shadow-sm bg-white">
            <CardHeader className="flex flex-row items-center justify-between border-b border-gray-100 bg-gray-50 p-6">
              <CardTitle className="text-lg font-medium text-gray-800">
                Program Contacts
              </CardTitle>
              <div className="flex gap-2">
                <Button
                  size="sm"
                  className="flex items-center gap-2"
                  onClick={() => setIsAddContactDialogOpen(true)}
                >
                  <Plus className="h-4 w-4" /> Add Contact
                </Button>
              </div>
            </CardHeader>
            <CardContent className="p-6">
              {programContacts.length === 0 ? (
                <div className="text-center py-8 text-gray-500">
                  <div className="mx-auto w-32 h-32 mb-4">
                    <img
                      src="/lovable-uploads/4c75fc6c-0171-49e7-9e79-ed3e2d9d4c44.png"
                      alt="No contacts"
                      className="w-full h-full object-contain"
                    />
                  </div>
                  <p className="text-lg font-medium mb-1">No Contacts Found</p>
                  <p className="mb-4">
                    No contacts are currently added to this program.
                  </p>
                  <Button
                    size="sm"
                    variant="outline"
                    className=" gap-2"
                    onClick={() => setIsAddContactDialogOpen(true)}
                  >
                    <Plus className="h-4 w-4" /> Create Your First Contact
                  </Button>
                </div>
              ) : (
                <div className="space-y-6">
                  {programContacts.map((contact, index) => (
                    <div
                      key={contact?.id}
                      className="flex flex-col sm:flex-row sm:items-center justify-between pb-4 border-b border-gray-100 last:border-0 last:pb-0"
                    >
                      <div className="mb-2 sm:mb-0">
                        <p className="font-medium text-gray-800">
                          {contact?.contactName}
                        </p>
                        <p className="text-sm text-gray-500">
                          {contact?.contactRole}
                        </p>
                      </div>
                      <div className="flex items-center gap-4">
                        <div className="flex items-center text-gray-500">
                          <Mail className="h-4 w-4 mr-1" />
                          <span className="text-sm">
                            {contact?.contactEmail}
                          </span>
                        </div>
                        {contact?.programContactPhoneVOList?.[0] && (
                          <div className="flex items-center text-gray-500">
                            <Phone className="h-4 w-4 mr-1" />
                            <span className="text-sm">
                              {
                                contact?.programContactPhoneVOList?.[0]
                                  .phoneNumber
                              }
                            </span>
                          </div>
                        )}
                        <Button
                          variant="ghost"
                          size="sm"
                          className="text-gray-500 hover:text-red-600 p-1 h-auto"
                          onClick={() => handleDeleteContact(contact)}
                        >
                          <Trash2 className="h-4 w-4" />
                        </Button>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </CardContent>
          </Card>
        </TabsContent>

        {/* Documents Tab */}
        <TabsContent value="documents" className="space-y-6 pt-4">
          <Card className="border-0 shadow-sm bg-white">
            <CardHeader className="border-b border-gray-100 bg-gray-50 flex flex-row items-center justify-between">
              <CardTitle className="text-lg font-medium text-gray-800">
                Program Documents
              </CardTitle>
              <div>
                <Button
                  size="sm"
                  className="relative"
                  onClick={() => setIsDocumentUploadOpen(true)}
                >
                  <FilePlus className="h-4 w-4 mr-2" /> Upload Document
                </Button>
              </div>
            </CardHeader>
            <CardContent className="p-6">
              {documents.length === 0 ? (
                <div className="text-center py-8 text-gray-500">
                  <p>No documents available. Upload your first document.</p>
                </div>
              ) : (
                <div className="divide-y divide-gray-100">
                  {documents.map((doc) => (
                    <div
                      key={doc.id}
                      className="py-4 flex flex-col sm:flex-row sm:items-center justify-between first:pt-0 last:pb-0"
                    >
                      <div className="flex items-center">
                        {isPdfFile(doc.name) ? (
                          <span className="mr-3 text-red-500">
                            <FileText className="h-5 w-5" />
                          </span>
                        ) : (
                          <span className="mr-3 text-blue-500">
                            <FileText className="h-5 w-5" />
                          </span>
                        )}
                        <div>
                          <p className="font-medium text-gray-800">
                            {doc.name}
                          </p>
                          <p className="text-sm text-gray-500 mt-0.5">
                            Uploaded by {doc.uploadedBy} •{" "}
                            {formatRelativeTime(doc.uploadedDate)}
                          </p>
                        </div>
                      </div>
                      <div className="flex items-center mt-2 sm:mt-0">
                        <span className="text-sm text-gray-500 mr-4">
                          {doc.size}
                        </span>
                        <Button
                          variant="outline"
                          size="sm"
                          onClick={() => handleDownloadDocument(doc)}
                          disabled={downloadingDocumentId === doc.id}
                        >
                          {downloadingDocumentId === doc.id ? (
                            <>
                              <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                              Downloading...
                            </>
                          ) : (
                            <>
                              <Download className="h-4 w-4 mr-2" />
                              Download
                            </>
                          )}
                        </Button>
                        <Button
                          variant="ghost"
                          size="sm"
                          className="text-red-600 hover:text-red-700 ml-2"
                          onClick={() => handleDeleteDocument(doc)}
                        >
                          <Trash2 className="h-4 w-4" />
                        </Button>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </CardContent>
          </Card>
        </TabsContent>

        {/* Activity Tab */}
        <TabsContent value="activity" className="pt-4">
          <Card className="border-0 shadow-sm bg-white">
            <CardHeader className="border-b border-gray-100 bg-gray-50">
              <CardTitle className="text-lg font-medium text-gray-800">
                Activity Log
              </CardTitle>
            </CardHeader>
            <CardContent className="p-6">
              {activityLoading ? (
                <div className="text-center text-gray-500">
                  Loading activity...
                </div>
              ) : activityError ? (
                <div className="text-center text-red-500">{activityError}</div>
              ) : activityLogs.length === 0 ? (
                <div className="text-center text-gray-500">
                  No activity found.
                </div>
              ) : (
                <div className="divide-y divide-gray-100">
                  {activityLogs.map((log) => (
                    <div key={log.id} className="py-4 first:pt-0 last:pb-0">
                      <div className="flex items-start gap-3">
                        <div className="p-2 rounded-md bg-blue-50 text-blue-600 mt-1">
                          <Activity className="h-4 w-4" />
                        </div>
                        <div className="flex-1 min-w-0">
                          {/* Event Name (Action) as badge */}
                          <div className="flex items-center gap-2 mb-1">
                            <span className="bg-gray-100 text-gray-600 px-2 py-0.5 rounded text-xs font-medium">
                              {log.action
                                .replace(/_/g, " ")
                                .toLowerCase()
                                .replace(/\b\w/g, (l) => l.toUpperCase())}
                            </span>
                          </div>
                          {/* Description */}
                          <div
                            className="text-gray-700 text-sm mb-1 truncate"
                            title={log.description}
                          >
                            {log.description}
                          </div>
                          {/* Meta: Who did what and when */}
                          <div className="flex flex-wrap gap-2 items-center text-xs text-gray-500 mt-1">
                            <span
                              title={log.actorName}
                              className="font-medium text-gray-600"
                            >
                              {log.actorName}
                            </span>
                            <span className="text-gray-400">•</span>
                            <span>
                              {log.createdAt
                                ? formatRelativeTime(log.createdAt)
                                : ""}
                            </span>
                          </div>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>

      {/* Dialog for adding new contact */}
      <Dialog
        open={isAddContactDialogOpen}
        onOpenChange={setIsAddContactDialogOpen}
      >
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle>Add New Contact</DialogTitle>
          </DialogHeader>
          <Form {...contactForm}>
            <form
              onSubmit={contactForm.handleSubmit(handleAddContact)}
              className="space-y-4"
            >
              <FormField
                control={contactForm.control}
                name="name"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Name</FormLabel>
                    <FormControl>
                      <Input placeholder="Enter contact name" {...field} />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />
              <FormField
                control={contactForm.control}
                name="email"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Email*</FormLabel>
                    <FormControl>
                      <Input placeholder="Enter contact email" {...field} />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />
              <FormField
                control={contactForm.control}
                name="role"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Role*</FormLabel>
                    <FormControl>
                      <Input placeholder="Enter contact role" {...field} />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />
              <div className="grid grid-cols-[110px_1fr] gap-2">
                <FormField
                  control={contactForm.control}
                  name="phoneCountry"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>Phone</FormLabel>
                      <FormControl>
                        <CountrySelect
                          value={field?.value}
                          onChange={field.onChange}
                        />
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />
                <FormField
                  control={contactForm.control}
                  name="phoneNumber"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel className="opacity-0">Number</FormLabel>
                      <FormControl>
                        <Input placeholder="Enter phone number" {...field} />
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />
              </div>
              <DialogFooter className="sm:justify-end">
                <Button
                  type="button"
                  variant="outline"
                  onClick={() => setIsAddContactDialogOpen(false)}
                >
                  Cancel
                </Button>
                <LoaderButton type="submit" loading={addingContact}>
                  <Plus className="h-4 w-4 mr-2" /> Add Contact
                </LoaderButton>
              </DialogFooter>
            </form>
          </Form>
        </DialogContent>
      </Dialog>

      {/* Dialog for linking new merchant - Updated with searchable dropdown */}
      <Dialog
        open={isAddMerchantDialogOpen}
        onOpenChange={setIsAddMerchantDialogOpen}
      >
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle>Link Merchants</DialogTitle>
            <DialogDescription>
              Select merchants to link to this program.
            </DialogDescription>
          </DialogHeader>

          <div className="my-4">
            <div className="mb-2">
              <p className="text-sm font-medium mb-1">Selected Merchants:</p>
              <div className="flex flex-wrap gap-1 mb-2">
                {selectedMerchants.length === 0 ? (
                  <p className="text-sm text-gray-500">No merchants selected</p>
                ) : (
                  selectedMerchants.map((merchantId) => {
                    const merchant = availableMerchants.find(
                      (m) => m.id === merchantId
                    );
                    return merchant ? (
                      <Badge
                        key={merchantId}
                        variant="secondary"
                        className="flex items-center gap-1"
                      >
                        {merchant.name}
                        <button
                          onClick={() => toggleMerchantSelection(merchantId)}
                          className="ml-1 h-3 w-3 rounded-full bg-gray-400 text-white flex items-center justify-center"
                        >
                          <X className="h-2 w-2" />
                        </button>
                      </Badge>
                    ) : null;
                  })
                )}
              </div>
            </div>

            <div className="max-h-60 overflow-hidden">
              <Command className="rounded-lg border shadow-md">
                <CommandInput
                  placeholder="Search merchants..."
                  value={searchQuery}
                  onValueChange={setSearchQuery}
                />
                <CommandList className="max-h-40 overflow-auto">
                  <CommandEmpty>No merchants found.</CommandEmpty>
                  <CommandGroup>
                    {filteredAvailableMerchants.length === 0 ? (
                      <div className="py-6 text-center text-sm">
                        No available merchants to link
                      </div>
                    ) : (
                      filteredAvailableMerchants.map((merchant) => (
                        <CommandItem
                          key={merchant.id}
                          onSelect={() => toggleMerchantSelection(merchant.id)}
                          className="flex items-center gap-2 py-3"
                        >
                          <div
                            className={`flex h-5 w-5 items-center justify-center rounded-md border ${
                              selectedMerchants.includes(merchant.id)
                                ? "bg-primary border-primary"
                                : "border-gray-300"
                            }`}
                          >
                            {selectedMerchants.includes(merchant.id) && (
                              <Check className="h-3 w-3 text-white" />
                            )}
                          </div>
                          <div className="flex items-center gap-3 flex-1">
                            <div className="h-8 w-8 rounded bg-gray-100 flex items-center justify-center">
                              <img
                                src={merchant.logo}
                                alt={merchant.name}
                                className="max-h-6 max-w-6"
                              />
                            </div>
                            <div>
                              <p className="text-sm font-medium">
                                {merchant.name}
                              </p>
                              <p className="text-xs text-gray-500">
                                {merchant.type}
                              </p>
                            </div>
                          </div>
                        </CommandItem>
                      ))
                    )}
                  </CommandGroup>
                </CommandList>
              </Command>
            </div>
          </div>

          <DialogFooter className="sm:justify-end">
            <Button
              type="button"
              variant="outline"
              onClick={() => {
                setIsAddMerchantDialogOpen(false);
                setSelectedMerchants([]);
                setSearchQuery("");
              }}
            >
              Cancel
            </Button>
            <Button
              onClick={handleAddMerchant}
              disabled={selectedMerchants.length === 0}
            >
              Link {selectedMerchants.length}{" "}
              {selectedMerchants.length === 1 ? "Merchant" : "Merchants"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Link Confirmation Dialog */}
      <Dialog
        open={isLinkConfirmationOpen}
        onOpenChange={setIsLinkConfirmationOpen}
      >
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle>Confirm Merchant Linking</DialogTitle>
            <DialogDescription>
              Are you sure you want to link {selectedMerchants.length}{" "}
              {selectedMerchants.length === 1 ? "merchant" : "merchants"} to
              this program?
            </DialogDescription>
          </DialogHeader>
          <DialogFooter className="sm:justify-end">
            <Button
              type="button"
              variant="outline"
              onClick={() => setIsLinkConfirmationOpen(false)}
            >
              Cancel
            </Button>
            <Button onClick={confirmLinkMerchants}>
              Link {selectedMerchants.length === 1 ? "Merchant" : "Merchants"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Delink Confirmation Dialog */}
      <DelinkConfirmationDialog
        open={isDelinkConfirmationOpen}
        onOpenChange={setIsDelinkConfirmationOpen}
        onConfirm={confirmDelinkMerchant}
        itemName={merchants.find((m) => m.id === merchantToDelink)?.name || ""}
        itemType="merchant"
      />

      {/* Contact Delete Confirmation Dialog */}
      <ContactDeleteConfirmationDialog
        open={isContactDeleteConfirmationOpen}
        onOpenChange={setIsContactDeleteConfirmationOpen}
        onConfirm={confirmDeleteContact}
        contactName={contactToDelete?.contactName || ""}
      />

      {/* Document Upload Dialog */}
      <Dialog
        open={isDocumentUploadOpen}
        onOpenChange={setIsDocumentUploadOpen}
      >
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle>Upload Document</DialogTitle>
          </DialogHeader>
          <Form {...documentUploadForm}>
            <form
              onSubmit={documentUploadForm.handleSubmit(handleDocumentUpload)}
              className="space-y-4"
            >
              <FormField
                control={documentUploadForm.control}
                name="documentName"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Document Name</FormLabel>
                    <FormControl>
                      <Input placeholder="Enter document name" {...field} />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />
              <FormField
                control={documentUploadForm.control}
                name="file"
                render={({ field: { value, onChange, ...fieldProps } }) => {
                  function truncateFileName(name, max = 32) {
                    if (name.length <= max) return name;
                    const extIndex = name.lastIndexOf(".");
                    const ext = extIndex !== -1 ? name.slice(extIndex) : "";
                    const base = name.slice(
                      0,
                      extIndex !== -1 ? extIndex : undefined
                    );
                    const keep = Math.max(8, max - ext.length - 5);
                    return base.slice(0, keep) + "..." + base.slice(-5) + ext;
                  }
                  return (
                    <FormItem>
                      <FormLabel>File</FormLabel>
                      <FormControl>
                        <div className="space-y-3">
                          {!value ? (
                            <div
                              className="border-2 border-dashed border-gray-300 rounded-lg p-6 text-center hover:border-gray-400 transition-colors cursor-pointer"
                              onClick={() =>
                                document
                                  .getElementById("document-file-input-reward")
                                  ?.click()
                              }
                            >
                              <Upload className="h-8 w-8 mx-auto mb-2 text-gray-400" />
                              <p className="text-sm text-gray-600 mb-1">
                                Click to select a file or drag and drop
                              </p>
                              <p className="text-xs text-gray-500">
                                PDF, DOC, DOCX, XLS, XLSX up to 10MB
                              </p>
                            </div>
                          ) : value instanceof File ? (
                            <div className="flex items-center gap-2 p-3 bg-gray-50 rounded-md min-w-0">
                              <FileText className="h-4 w-4 text-blue-500 flex-shrink-0" />
                              <div className="flex-1 min-w-0">
                                <span
                                  className="text-sm text-gray-700 block truncate"
                                  title={value.name}
                                >
                                  {truncateFileName(value.name)}
                                </span>
                              </div>
                              <Button
                                type="button"
                                variant="ghost"
                                size="sm"
                                onClick={(e) => {
                                  e.stopPropagation();
                                  onChange(null);
                                  const fileInput = document.getElementById(
                                    "document-file-input-reward"
                                  ) as HTMLInputElement;
                                  if (fileInput) {
                                    fileInput.value = "";
                                  }
                                }}
                                className="text-red-500 hover:text-red-700 flex-shrink-0"
                              >
                                <Trash2 className="h-3 w-3" />
                              </Button>
                            </div>
                          ) : null}
                          <input
                            id="document-file-input-reward"
                            type="file"
                            className="hidden"
                            accept=".pdf,.doc,.docx,.xls,.xlsx"
                            onChange={(e) => {
                              const file = e.target.files?.[0];
                              if (file) {
                                if (file.size > 10 * 1024 * 1024) {
                                  toast.error("File size exceeds 10MB limit.");
                                  return;
                                }
                                onChange(file);
                              }
                            }}
                            {...fieldProps}
                          />
                        </div>
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  );
                }}
              />
              <DialogFooter className="sm:justify-end">
                <Button
                  type="button"
                  variant="outline"
                  onClick={() => setIsDocumentUploadOpen(false)}
                >
                  Cancel
                </Button>
                <LoaderButton type="submit" loading={isDocumentUploading}>
                  Upload
                </LoaderButton>
              </DialogFooter>
            </form>
          </Form>
        </DialogContent>
      </Dialog>

      {/* Document Delete Confirmation Dialog */}
      <Dialog
        open={isDeleteDocumentOpen}
        onOpenChange={setIsDeleteDocumentOpen}
      >
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle>Delete Document</DialogTitle>
            <DialogDescription>
              Are you sure you want to delete this document?
            </DialogDescription>
          </DialogHeader>
          <DialogFooter className="sm:justify-end">
            <Button
              type="button"
              variant="outline"
              onClick={() => setIsDeleteDocumentOpen(false)}
            >
              Cancel
            </Button>
            <Button type="submit" onClick={confirmDeleteDocument}>
              Delete
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default RewardProgramDetails;
